import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ImageUpload } from '@/components/ui/image-upload'
import { ApiError, deleteJSON, postFormData } from '@/lib/api'

type ProfilePhoto = { filename: string; thumbnail_url: string }

export function MemberPortalProfilePhoto({ photo }: { photo?: ProfilePhoto }) {
  const queryClient = useQueryClient()
  const [removeOpen, setRemoveOpen] = useState(false)
  const upload = useMutation({
    mutationFn: (file: File) => {
      const body = new FormData()
      body.append('file', file)
      return postFormData('/api/v1/member_portal/profile-photo', body)
    },
    onSuccess: refreshProfile,
  })
  const remove = useMutation({
    mutationFn: () => deleteJSON('/api/v1/member_portal/profile-photo'),
    onSuccess: () => {
      setRemoveOpen(false)
      refreshProfile()
    },
  })
  const error = upload.error ?? remove.error

  function refreshProfile() {
    queryClient.invalidateQueries({ queryKey: ['member-portal-profile'] })
  }

  return (
    <section
      className="border-b border-border px-5 py-5 sm:px-6"
      aria-labelledby="profile-photo-heading"
    >
      <h2 id="profile-photo-heading" className="text-body font-medium text-foreground">
        Profile Photo
      </h2>
      <p className="mb-3 text-body-sm text-muted-foreground">JPEG, PNG, or WebP, up to 5 MB.</p>
      <ImageUpload
        image={photo ? { filename: photo.filename, url: photo.thumbnail_url } : null}
        label="Profile Photo"
        pending={upload.isPending || remove.isPending}
        onChoose={upload.mutate}
        onRemove={() => setRemoveOpen(true)}
      />
      {error && <Alert>{profilePhotoErrorMessage(error)}</Alert>}
      <Dialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Profile Photo?</DialogTitle>
            <DialogDescription>This deletes the image and cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRemoveOpen(false)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={() => remove.mutate()}>
              Remove photo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}

function profilePhotoErrorMessage(error: Error): string {
  if (error instanceof ApiError && typeof error.body === 'object' && error.body !== null) {
    const message = (error.body as { errors?: { profile_photo?: string[] } }).errors
      ?.profile_photo?.[0]
    if (message) return message
  }
  return error.message
}
